"use client"

import { Image } from "@/app/components/image"
import { cn } from "@rarible_int/anaconda_ui_components"
import { type PointerEvent, useRef, useState } from "react"

const LENS_ZOOM = 2.35
const LENS_SIZE_PX = 176

type GachaZoomableCardPreviewProps = {
  image: string
  alt: string
  className?: string
}

export function GachaZoomableCardPreview({
  image,
  alt,
  className,
}: GachaZoomableCardPreviewProps) {
  const previewFrameRef = useRef<HTMLDivElement>(null)
  const [lensActive, setLensActive] = useState(false)
  const [lensPosition, setLensPosition] = useState({
    centerX: 50,
    centerY: 50,
    imageHeight: 1,
    imageWidth: 1,
    imageX: 0,
    imageY: 0,
  })

  const updateLensPosition = (clientX: number, clientY: number) => {
    const frame = previewFrameRef.current
    const img = frame?.querySelector("img")
    if (!frame || !img) return

    const container = frame.parentElement
    const containerRect = container?.getBoundingClientRect()
    const frameRect = frame.getBoundingClientRect()
    if (!containerRect) return

    const naturalRatio =
      img.naturalWidth > 0 && img.naturalHeight > 0
        ? img.naturalWidth / img.naturalHeight
        : 1
    const frameRatio = frameRect.width / frameRect.height
    const renderedWidth =
      frameRatio > naturalRatio
        ? frameRect.height * naturalRatio
        : frameRect.width
    const renderedHeight =
      frameRatio > naturalRatio
        ? frameRect.height
        : frameRect.width / naturalRatio
    const renderedLeft = frameRect.left + (frameRect.width - renderedWidth) / 2
    const renderedTop = frameRect.top + (frameRect.height - renderedHeight) / 2
    const clampedX = Math.min(
      renderedLeft + renderedWidth,
      Math.max(renderedLeft, clientX),
    )
    const clampedY = Math.min(
      renderedTop + renderedHeight,
      Math.max(renderedTop, clientY),
    )

    setLensPosition({
      centerX: clampedX - containerRect.left,
      centerY: clampedY - containerRect.top,
      imageHeight: renderedHeight,
      imageWidth: renderedWidth,
      imageX: clampedX - renderedLeft,
      imageY: clampedY - renderedTop,
    })
  }

  const activateLens = (event: PointerEvent<HTMLDivElement>) => {
    setLensActive(true)
    updateLensPosition(event.clientX, event.clientY)
  }

  return (
    <div
      className={cn(
        "group relative flex aspect-square w-full cursor-crosshair touch-none select-none items-center justify-center overflow-hidden rounded-xl border border-foreground/10 bg-[#0F0F0F] p-3 [-webkit-touch-callout:none] [&_img]:select-none [&_img]:[-webkit-user-drag:none] sm:p-4",
        className,
      )}
      onPointerEnter={(event) => {
        if (event.pointerType !== "mouse") return
        activateLens(event)
      }}
      onPointerDown={(event) => {
        if (event.pointerType !== "mouse") {
          event.currentTarget.setPointerCapture(event.pointerId)
        }
        activateLens(event)
      }}
      onPointerMove={(event) => {
        if (!lensActive && event.pointerType !== "mouse") return
        updateLensPosition(event.clientX, event.clientY)
      }}
      onPointerUp={(event) => {
        if (event.pointerType !== "mouse") {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId)
          }
          activateLens(event)
        }
      }}
      onPointerCancel={(event) => {
        if (event.pointerType === "mouse") return
        setLensActive(true)
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === "mouse") {
          setLensActive(false)
        }
      }}
    >
      <div
        ref={previewFrameRef}
        data-gacha-result-card
        className="relative h-full max-h-full w-full max-w-full"
      >
        <Image
          imageSrc={image}
          alt={alt}
          className="object-contain"
          containerClassName="h-full w-full"
        />
      </div>
      {lensActive ? (
        <div
          className="pointer-events-none absolute z-20 size-32 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full border border-brand bg-[#0F0F0F] shadow-[0_18px_48px_rgba(0,0,0,0.45),inset_0_0_0_1px_rgba(255,255,255,0.18)] sm:size-44"
          style={{
            left: lensPosition.centerX,
            top: lensPosition.centerY,
          }}
        >
          <img
            src={image}
            alt=""
            className="absolute max-w-none select-none"
            style={{
              height: lensPosition.imageHeight * LENS_ZOOM,
              left: LENS_SIZE_PX / 2 - lensPosition.imageX * LENS_ZOOM,
              top: LENS_SIZE_PX / 2 - lensPosition.imageY * LENS_ZOOM,
              width: lensPosition.imageWidth * LENS_ZOOM,
            }}
          />
        </div>
      ) : null}
    </div>
  )
}
